import { DirectionsRun, SportsScore } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, IconButton, styled, Tooltip, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useExerciseSession } from "../ExerciseSessionProvider";
import { useLogging } from "../logging/useLogging";
import { AppEvent } from "../types";
import { secondsToTime } from "../utils/timeHelpers";
import { ActiveSessionContainer, HeaderButtons } from "./Exercise.sc";

interface ExerciseHeaderProps {
    onNextButtonClick: (value?: boolean) => void;
    onEndButtonClick: () => void;
    loading?: boolean;
    disableNextButton?: boolean;
}

const ExerciseHeader: React.FunctionComponent<ExerciseHeaderProps> = ({ onNextButtonClick, onEndButtonClick, loading, disableNextButton }) => {
    const { t } = useTranslation();

    const { durationOfAllExercises } = useExerciseSession();
    const { log } = useLogging();

    const [currentSessionStart, setCurrentSessionStart] = useState(Date.now());
    const [currentMillis, setCurrentMillis] = useState(Date.now());

    const timePassed = useMemo(
        () => durationOfAllExercises + (currentMillis - currentSessionStart) / 1000,
        [durationOfAllExercises, currentSessionStart, currentMillis],
    );

    const handleSkipButtonClick = () => {
        log(AppEvent.ExerciseSkipped);
        onNextButtonClick(true);
    };

    const handleNextButtonClick = () => {
        log(AppEvent.ExerciseFinished);
        onNextButtonClick();
    };

    const handleEndButtonClick = () => {
        log(AppEvent.ExerciseFinished);
        log(AppEvent.SessionCompleted);
        onEndButtonClick();
    };

    React.useEffect(() => {
        if (!loading) {
            const interval = setInterval(() => {
                setCurrentMillis(Date.now());
            }, 500);
            setCurrentMillis(Date.now());
            setCurrentSessionStart(Date.now());

            return () => clearInterval(interval);
        }
    }, [loading, durationOfAllExercises]);

    return (
        <>
            <StickyHeader>
                <ActiveSessionContainer>
                    <Typography>
                        {t("duration")}: {secondsToTime(timePassed)} min
                    </Typography>
                    <Button endIcon={<SportsScore />} color="inherit" onClick={handleEndButtonClick} variant="text">
                        {t("end")}
                    </Button>
                </ActiveSessionContainer>
                <HeaderButtons>
                    <Button style={{ color: "black", paddingLeft: 0 }} onClick={handleSkipButtonClick} disabled={loading || disableNextButton}>
                        {t("skip")}
                    </Button>
                    <div>
                        {disableNextButton && (
                            <Tooltip
                                disableFocusListener
                                title={t("tooltip_next_disabled")}
                                enterTouchDelay={0}
                                leaveTouchDelay={5000}
                                placement="top"
                                arrow
                            >
                                <IconButton style={{ padding: 0, marginRight: 8 }}>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Button
                            endIcon={<DirectionsRun />}
                            color="primary"
                            variant="contained"
                            onClick={handleNextButtonClick}
                            disabled={loading || disableNextButton}
                        >
                            {t("next")}
                        </Button>
                    </div>
                </HeaderButtons>
            </StickyHeader>
        </>
    );
};

export default ExerciseHeader;

const StickyHeader = styled("div")`
    position: sticky;
    top: 56px;
    background-color: white;
    z-index: 10;
    width: 100%;
    max-width: 700px;
    padding-bottom: 15px;
`;
